import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, MapPin, ExternalLink, Briefcase } from "lucide-react";
import { api } from "@/lib/api";
import { fmtUSD, relativeTime } from "@/lib/utils";
import Avatar from "@/components/Avatar";
import ReviewStatusBadge from "@/components/ReviewStatusBadge";
import { SecureImage } from "@/components/ImageUploader";
import { VerifiedBadge } from "@/components/VerifiedBadges";
import { DeveloperAccountActions, DevelopmentQuickActions } from "@/components/AdminQuickActions";

const DOC_STATUS_LABEL = { not_started: "Not started", incomplete: "Incomplete", pending_review: "Under review", action_required: "Action required", approved: "Approved" };

function DeveloperPicker({ selected, onPick }) {
  const [devs, setDevs] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { (async () => { const { data } = await api.get("/admin/users?role=developer"); setDevs(data); })(); }, []);
  const list = devs.filter((d) => `${d.name} ${d.email} ${d.company_name || ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="soft-card p-3 lg:sticky lg:top-24" data-testid="dev-view-picker">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input data-testid="dev-view-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search developers…" className="w-full pl-9 pr-3 py-2 rounded-xl border border-neutral-200 text-sm outline-none focus:border-propera" />
      </div>
      <div className="mt-2 max-h-[60vh] overflow-y-auto space-y-0.5">
        {list.map((d) => (
          <button key={d.id} data-testid={`dev-view-pick-${d.id}`} onClick={() => onPick(d.id)} className={`w-full text-left flex items-center gap-2.5 px-2.5 py-2 rounded-xl transition-colors ${selected === d.id ? "bg-propera-light" : "hover:bg-neutral-50"}`}>
            <Avatar name={d.name} src={d.avatar} size={30} />
            <span className="min-w-0">
              <span className="block text-sm font-semibold truncate">{d.company_name || d.name}</span>
              <span className="block text-[11px] text-neutral-500 truncate">{d.email}</span>
            </span>
          </button>
        ))}
        {list.length === 0 && <p className="p-4 text-xs text-neutral-500 text-center">No developers found.</p>}
      </div>
    </div>
  );
}

function Kpi({ label, value, testId }) {
  return <div className="soft-card p-4" data-testid={testId}><p className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{label}</p><p className="text-2xl font-bold tabular mt-1">{value ?? 0}</p></div>;
}

function DeveloperSnapshot({ id }) {
  const [data, setData] = useState(null);
  async function load() { const { data } = await api.get(`/admin/view/developer/${id}`); setData(data); }
  useEffect(() => { setData(null); load(); }, [id]);
  if (!data) return <p className="text-sm text-neutral-500 py-10 text-center">Loading developer…</p>;
  const { user: u, dashboard: k, developments, leads, company_verification: cv } = data;
  return (
    <div className="space-y-8" data-testid="dev-view-snapshot">
      <div className="soft-card p-5 flex items-start gap-4 flex-wrap">
        <Avatar name={u.name} src={u.avatar} size={56} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold" data-testid="dev-view-name">{u.company_name || u.name}</h2>
            {u.verified && <VerifiedBadge kind="developer" />}
            {u.suspended && <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-700">Suspended</span>}
          </div>
          <p className="text-xs text-neutral-500 mt-1">{u.name} · {u.email} · {u.country || "—"} · joined {relativeTime(u.created_at)}</p>
          <p className="text-xs text-neutral-600 mt-2" data-testid="dev-view-company-verification">Company verification ({cv.country || "—"}): <b>{DOC_STATUS_LABEL[cv.overall_status]}</b> · {cv.required_approved}/{cv.required_total} approved, {cv.required_uploaded}/{cv.required_total} uploaded</p>
          <div className="mt-3"><DeveloperAccountActions user={u} companyReady={cv.all_approved} onDone={load} /></div>
        </div>
        <div className="flex gap-2">
          <Link to={`/u/${u.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-neutral-200 text-xs font-semibold hover:border-propera hover:text-propera">Public profile <ExternalLink size={12} /></Link>
          <Link to="/admin?tab=verification" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-neutral-200 text-xs font-semibold hover:border-propera hover:text-propera">Review documents</Link>
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-3">Dashboard</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Developments" value={k.developments} testId="dev-view-kpi-developments" />
          <Kpi label="Interested investors" value={k.investors} testId="dev-view-kpi-investors" />
          <Kpi label="Allocated units" value={k.allocated_units} testId="dev-view-kpi-allocated" />
          <Kpi label="New interests" value={k.new_interests} testId="dev-view-kpi-new" />
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-3">Properties ({developments.length})</p>
        {developments.length === 0 ? <p className="soft-card p-6 text-sm text-neutral-500 text-center">No developments yet.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {developments.map((d) => (
              <div key={d.id} className="soft-card overflow-hidden flex flex-col">
              <Link to={`/development/${d.id}`} data-testid={`dev-view-property-${d.id}`} className="block hover:opacity-95 transition-opacity">
                <div className="relative"><SecureImage src={d.cover_image} alt="" className="w-full h-36 object-cover" /><span className="absolute top-3 left-3"><ReviewStatusBadge status={d.review_status} /></span></div>
                <div className="p-4">
                  <p className="font-semibold">{d.name}</p>
                  <p className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5"><MapPin size={11} /> {d.city}, {d.country}</p>
                  <p className="text-sm font-bold text-propera mt-2 tabular">{fmtUSD(d.starting_price)}</p>
                  <p className="text-[11px] text-neutral-500 mt-2">{d.unit_count} units · {d.interest_count} interests · docs {d.docs.required_approved}/{d.docs.required_total} approved</p>
                </div>
              </Link>
              <div className="px-4 pb-4 mt-auto"><DevelopmentQuickActions dev={d} onDone={load} /></div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-3">Leads ({leads.length})</p>
        <div className="soft-card overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-neutral-50 text-xs text-neutral-500 uppercase tracking-wider"><tr><th className="text-left px-5 py-3">Investor</th><th className="text-left px-5 py-3">Development</th><th className="text-left px-5 py-3">Stage</th><th className="text-left px-5 py-3">When</th></tr></thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-neutral-100" data-testid={`dev-view-lead-${l.id}`}>
                  <td className="px-5 py-3"><Link to={`/u/${l.investor_id}`} className="hover:text-propera font-medium">{l.investor?.name || "—"}</Link><p className="text-xs text-neutral-500">{l.investor?.email}</p></td>
                  <td className="px-5 py-3">{l.development_name || "—"}</td>
                  <td className="px-5 py-3 text-xs capitalize">{(l.status || "").replace(/_/g, " ")}</td>
                  <td className="px-5 py-3 text-xs text-neutral-500">{relativeTime(l.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {leads.length === 0 && <p className="p-6 text-sm text-neutral-500 text-center">No leads yet.</p>}
        </div>
      </div>
    </div>
  );
}

export default function AdminDeveloperView() {
  const [params, setParams] = useSearchParams();
  const id = params.get("id");
  return (
    <div className="max-w-7xl mx-auto" data-testid="admin-developer-view">
      <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold">Admin Console · View as</p>
      <h1 className="text-4xl font-bold text-neutral-900 mt-2">Developer view</h1>
      <p className="text-neutral-500 mt-2">Pick any developer to see their dashboard, properties and leads, read-only.</p>
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 mt-8 items-start">
        <DeveloperPicker selected={id} onPick={(v) => setParams({ id: v })} />
        {id ? <DeveloperSnapshot id={id} /> : (
          <div className="soft-card p-12 text-center text-sm text-neutral-500" data-testid="dev-view-empty"><Briefcase className="mx-auto mb-3 text-neutral-300" size={28} />Select a developer on the left.</div>
        )}
      </div>
    </div>
  );
}
